/**
 * Service codes (short codes) used throughout the application
 * These match the codes stored in the database
 */

// Core services with booking slots
export const CORE_SERVICES = {
  MAKEUP: 'makeup',
  DRESS: 'dress',
  PHOTO: 'photo',
} as const

// Add-on services without booking slots
export const ADDON_SERVICES = {
  ACCESSORIES: 'accessories',
  PROPS: 'props',
  COLOR_ADJUSTMENT: 'color_adjustment',
  SHUTTLE: 'shuttle',
} as const

// All services combined
export const SERVICES = {
  ...CORE_SERVICES,
  ...ADDON_SERVICES,
} as const

// Service type
export type ServiceCode = typeof SERVICES[keyof typeof SERVICES]
export type CoreServiceCode = typeof CORE_SERVICES[keyof typeof CORE_SERVICES]
export type AddonServiceCode = typeof ADDON_SERVICES[keyof typeof ADDON_SERVICES]

// Service codes array for easy iteration
export const ALL_SERVICE_CODES: ServiceCode[] = [
  'makeup',
  'dress',
  'photo',
  'accessories',
  'props',
  'color_adjustment',
  'shuttle',
]

export const CORE_SERVICE_CODES: CoreServiceCode[] = ['makeup', 'dress', 'photo']

// Service categories for validation
export const STUDIO_SERVICES: CoreServiceCode[] = ['makeup', 'dress']
export const WAT_ARUN_SERVICES: CoreServiceCode[] = ['photo']

// Service availability by package type
export const PACKAGE_TYPE_SERVICES = {
  cosmetic: ['makeup', 'accessories', 'props'] as ServiceCode[],
  photography: ['photo', 'color_adjustment'] as ServiceCode[],
  costume_set: [
    'makeup',
    'dress',
    'photo',
    'color_adjustment',
    'shuttle',
    'accessories',
    'props',
  ] as ServiceCode[],
}

// Default services for each package type
export const DEFAULT_SERVICES_BY_TYPE: Record<string, ServiceCode[]> = {
  cosmetic: ['makeup'],
  photography: ['photo'],
  costume_set: [],
}

// Service descriptions for admin
export const SERVICE_DESCRIPTIONS: Record<ServiceCode, string> = {
  makeup: 'Makeup & Hair',
  dress: 'Dress Rental',
  photo: 'Photography',
  accessories: 'Accessories',
  props: 'Props',
  color_adjustment: 'Color Adjustment',
  shuttle: 'Shuttle Service',
}

// Translation keys for services (used with i18n)
export const SERVICE_TRANSLATION_KEYS: Record<ServiceCode, string> = {
  makeup: 'thank_you.services.makeup',
  dress: 'thank_you.services.dress',
  photo: 'thank_you.services.photo',
  accessories: 'thank_you.services.accessories',
  props: 'thank_you.services.props',
  color_adjustment: 'thank_you.services.color_adjustment',
  shuttle: 'thank_you.services.shuttle',
}

// Helper functions
export function isCoreService(service: string): service is CoreServiceCode {
  return CORE_SERVICE_CODES.includes(service as CoreServiceCode)
}

export function isStudioService(service: string): boolean {
  return STUDIO_SERVICES.includes(service as CoreServiceCode)
}

export function isWatArunService(service: string): boolean {
  return WAT_ARUN_SERVICES.includes(service as CoreServiceCode)
}

export function hasPhotoService(services: string[]): boolean {
  return services.includes('photo')
}

export function hasMakeupService(services: string[]): boolean {
  return services.includes('makeup')
}

export function hasDressService(services: string[]): boolean {
  return services.includes('dress')
}

export function hasStudioService(services: string[]): boolean {
  return services.some(s => STUDIO_SERVICES.includes(s as CoreServiceCode))
}

export function calculateDeposit(services: string[]): number {
  if (hasPhotoService(services)) return 1000
  if (hasMakeupService(services) || hasDressService(services)) return 500
  return 0
}
